// src/lib/constants.js
import { LIFECYCLE_STAGES, STAGE_MAP } from "./lifecycleStages";
import { isLidarLeadContact } from "./deviceProfile";

export { LIFECYCLE_STAGES, STAGE_MAP };

/** CRM industry segment (manual + reporting). */
export const INDUSTRY_OPTIONS = [
  { id: "architect", label: "Architect" },
  { id: "designer", label: "Designer" },
  { id: "remodeler_contractor", label: "Remodeler / Contractor" },
  { id: "other", label: "Other" },
];

export const INDUSTRY_MAP = Object.fromEntries(INDUSTRY_OPTIONS.map((o) => [o.id, o.label]));

/** Enterprise deal funnel (Kanban on Deals page). */
export const DEAL_STAGES = [
  { id: "lead", label: "Lead", color: "#64748b" },
  { id: "demo", label: "Demo", color: "#3b82f6" },
  { id: "pilot", label: "Pilot", color: "#8b5cf6" },
  { id: "contracting", label: "Contracting", color: "#f59e0b" },
  { id: "won", label: "Won", color: "#10b981" },
  { id: "lost", label: "Lost", color: "#ef4444" },
];

export const DEAL_STAGE_MAP = Object.fromEntries(DEAL_STAGES.map((s) => [s.id, s]));

/** Map legacy deal stages (pre-funnel) to current ids for display + drag-drop. */
export const DEAL_STAGE_LEGACY_MAP = {
  discovery: "lead",
  proposal: "demo",
  negotiation: "contracting",
};

export function normalizeDealStage(stage) {
  if (!stage) return "lead";
  return DEAL_STAGE_LEGACY_MAP[stage] || stage;
}

export const DEAL_OPEN_STAGES = ["lead", "demo", "pilot", "contracting"];

export const TICKET_STATUS = [
  { id: "open", label: "Open" },
  { id: "pending", label: "Pending" },
  { id: "closed", label: "Closed" },
];

// ── Plan tiers (legacy — Scanbrix is per-project, not subscription SaaS) ──
export const PLAN_TIERS = [];

// ── Auto-tag rules (evaluated on contact save/update) ──
export const AUTO_TAG_RULES = [
  { tag: "High Volume", condition: (c) => (c.totalScans || 0) >= 10 },
  { tag: "Enterprise Spend", condition: (c) => (c.lifetimeRevenue ?? 0) >= 2000 },
  { tag: "Power User", condition: (c) => (c.usageEvents30d || 0) >= 500 },
  { tag: "At-Risk", condition: (c) => (c.healthScore || 100) < 40 },
  { tag: "LiDAR lead", condition: (c) => isLidarLeadContact(c) },
  { tag: "Sample interest", condition: (c) => c.stage === "sample_downloaded" || c.sampleDownloaded === true },
  { tag: "Trial order", condition: (c) => c.stage === "trial_order" || c.hasTrialOrder === true },
  { tag: "Paid user", condition: (c) => c.stage === "paid_user" || c.hasPaidOrder === true },
];

// ── Activity event types ──
export const ACTIVITY_TYPES = {
  stage_changed:          { icon: "→", color: "#3b82f6", label: "Stage changed" },
  note_added:             { icon: "✎", color: "#6b7280", label: "Note added" },
  task_created:           { icon: "✓", color: "#8b5cf6", label: "Task created" },
  task_completed:         { icon: "✔", color: "#10b981", label: "Task completed" },
  stripe_payment:         { icon: "$", color: "#10b981", label: "Payment" },
  stripe_payment_link:    { icon: "🔗", color: "#635bff", label: "Payment link" },
  stripe_refund:          { icon: "↩", color: "#f59e0b", label: "Refund" },
  stripe_payment_failed:  { icon: "!", color: "#ef4444", label: "Payment failed" },
  stripe_subscription:    { icon: "⟳", color: "#3b82f6", label: "Subscription" },
  firebase_session:       { icon: "◉", color: "#f59e0b", label: "Session" },
  firebase_event:         { icon: "⚡", color: "#f59e0b", label: "Usage event" },
  scan_uploaded:          { icon: "⬡", color: "#06b6d4", label: "Scan uploaded" },
  scan_started:           { icon: "◎", color: "#8b5cf6", label: "Started scanning" },
  scan_finished:          { icon: "◉", color: "#7c3aed", label: "Finished scanning" },
  onboarding_event:       { icon: "☰", color: "#0ea5e9", label: "Onboarding" },
  activation_prompt_event:{ icon: "▶", color: "#f59e0b", label: "Activation prompt" },
  product_analytics_event:{ icon: "⚡", color: "#8b5cf6", label: "Product event" },
  email_sent:             { icon: "✉", color: "#6b7280", label: "Email sent" },
  email_opened:           { icon: "✉", color: "#10b981", label: "Email opened" },
  contact_merged:         { icon: "⊕", color: "#8b5cf6", label: "Contact merged" },
  calendar_booking:       { icon: "◷", color: "#3b82f6", label: "Demo booked" },
  order_created:          { icon: "◎", color: "#10b981", label: "Order" },
  workspace_created:      { icon: "⬡", color: "#0ea5e9", label: "Workspace" },
  workspace_joined:       { icon: "◇", color: "#06b6d4", label: "Workspace" },
  conversion_job:         { icon: "↻", color: "#8b5cf6", label: "Conversion" },
  file_uploaded:          { icon: "▤", color: "#64748b", label: "File" },
  operator_job:           { icon: "⚙", color: "#f59e0b", label: "Operator" },
  audit_log:              { icon: "▸", color: "#94a3b8", label: "Audit" },
  correspondence:       { icon: "M", color: "#6366f1", label: "Correspondence" },
  deal_updated:         { icon: "◇", color: "#a855f7", label: "Deal" },
  email_sequence_step:  { icon: "S", color: "#8b5cf6", label: "Sequence" },
  email_delivered:      { icon: "✉", color: "#64748b", label: "Email delivered" },
  email_bounced:        { icon: "!", color: "#ef4444", label: "Email bounced" },
  fiam_message:         { icon: "◆", color: "#f97316", label: "In-app message" },
};

// ── Task priorities ──
export const TASK_PRIORITIES = ["low", "medium", "high", "urgent"];

// ── User roles ──
export const USER_ROLES = {
  admin:    { label: "Admin",     canEdit: true,  canDelete: true,  canManageUsers: true },
  sales:    { label: "Sales Rep", canEdit: true,  canDelete: false, canManageUsers: false },
  readonly: { label: "Read Only", canEdit: false, canDelete: false, canManageUsers: false },
};
